import { useState, useEffect, useRef, useMemo } from 'react';
import { useLogStream } from '../hooks/useLogStream';
import type { LogEntry } from '../lib/api';

type LogLevel = 'info' | 'warn' | 'error';

export function LogsPage() {
  const { entries, isLoading, isLiveTailing, clear } = useLogStream();
  const [activeLevels, setActiveLevels] = useState<Set<LogLevel>>(
    new Set(['info', 'warn', 'error'])
  );
  const [search, setSearch] = useState('');
  const [autoScroll, setAutoScroll] = useState(true);
  const logEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const toggleLevel = (level: LogLevel) => {
    setActiveLevels((prev) => {
      const next = new Set(prev);
      if (next.has(level)) {
        next.delete(level);
      } else {
        next.add(level);
      }
      return next;
    });
  };

  const filtered = useMemo(() => {
    let result: LogEntry[] = entries;

    // Filter by level
    result = result.filter((e) => activeLevels.has(e.level));

    // Filter by search
    if (search) {
      const lower = search.toLowerCase();
      result = result.filter((e) => e.message.toLowerCase().includes(lower));
    }

    return result;
  }, [entries, activeLevels, search]);

  // Auto-scroll to bottom when new entries arrive
  useEffect(() => {
    if (autoScroll && logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [filtered, autoScroll]);

  // Detect if user scrolled up (disable auto-scroll)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 60;
      setAutoScroll(isAtBottom);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const levelColors: Record<LogLevel, string> = {
    info: 'text-gray-400',
    warn: 'text-yellow-400',
    error: 'text-red-400',
  };

  const levelBgColors: Record<LogLevel, string> = {
    info: 'bg-gray-800 text-gray-300',
    warn: 'bg-yellow-900/50 text-yellow-300',
    error: 'bg-red-900/50 text-red-300',
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b border-gray-800 px-4 py-3 md:px-6 md:py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Logs</h1>
            <p className="mt-0.5 text-xs text-gray-500">
              {filtered.length} entries
              {isLiveTailing && (
                <span className="ml-2 inline-flex items-center gap-1">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-500" />
                  Live
                </span>
              )}
            </p>
          </div>
          <button
            onClick={clear}
            className="rounded-lg border border-gray-700 px-3 py-1.5 text-xs text-gray-400 transition-colors hover:border-gray-600 hover:text-gray-200"
          >
            Clear
          </button>
        </div>

        {/* Filters */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {/* Level toggles */}
          <div className="flex gap-1">
            {(['info', 'warn', 'error'] as const).map((level) => (
              <button
                key={level}
                onClick={() => toggleLevel(level)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  activeLevels.has(level)
                    ? levelBgColors[level]
                    : 'bg-gray-900 text-gray-600'
                }`}
              >
                {level}
              </button>
            ))}
          </div>

          {/* Search */}
          <input
            type="text"
            placeholder="Filter logs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="min-w-0 flex-1 rounded-md border border-gray-700 bg-gray-900 px-3 py-1.5 text-xs text-gray-200 placeholder-gray-600 outline-none focus:border-gray-600"
          />
        </div>
      </div>

      {/* Log entries */}
      <div ref={containerRef} className="flex-1 overflow-auto bg-gray-950 font-mono text-xs">
        {isLoading && entries.length === 0 ? (
          <div className="flex h-full items-center justify-center text-gray-600">
            Loading logs...
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex h-full items-center justify-center text-gray-600">
            {entries.length === 0 ? 'No logs yet' : 'No matching entries'}
          </div>
        ) : (
          <div className="p-2">
            {filtered.map((entry) => (
              <div
                key={entry.id}
                className="flex gap-2 rounded px-2 py-0.5 hover:bg-gray-900/50"
              >
                <span className="shrink-0 text-gray-600">
                  {new Date(entry.timestamp).toLocaleTimeString()}
                </span>
                <span className={`shrink-0 w-10 text-right ${levelColors[entry.level]}`}>
                  {entry.level === 'info' ? 'INF' : entry.level === 'warn' ? 'WRN' : 'ERR'}
                </span>
                <span className="text-gray-300 break-all">{entry.message}</span>
              </div>
            ))}
            <div ref={logEndRef} />
          </div>
        )}
      </div>

      {/* Scroll to bottom indicator */}
      {!autoScroll && (
        <button
          onClick={() => {
            setAutoScroll(true);
            logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }}
          className="absolute bottom-4 right-8 rounded-lg bg-gray-800 px-3 py-1.5 text-xs text-gray-400 shadow-lg transition-colors hover:text-gray-200"
        >
          Scroll to bottom
        </button>
      )}
    </div>
  );
}
